(() => {
  const CART_SECTIONS = [
    { id: 'cart-head', selectors: ['[data-render-cart-bubble]', '[data-render-cart-text]'] },
    { id: 'cart-showcase', selectors: ['[data-line-items-drawer-container]'] }
  ];
  const DEMO_FRAME_PRICES = { S: 79000, M: 79000, L: 79000 };
  const FRAME_LOOKS = [
    { test: /cern/, look: 'black', label: 'Černý rám' },
    { test: /bil/, look: 'white', label: 'Bílý rám' },
    { test: /drev|dub/, look: 'oak', label: 'Dřevěný rám' }
  ];
  const PAGE_SIZE = 36;
  const AUTHOR_GAP = 4;
  const ADD_FAILED = 'Sadu se nepodařilo přidat do košíku. Zkuste to prosím znovu.';

  const fetchJson = async (url, attempts = 3) => {
    for(let attempt = 1; ; attempt++){
      try {
        const response = await fetch(url);
        if(!response.ok) throw new Error(`${url}: ${response.status}`);
        return await response.json();
      } catch(error) {
        if(attempt === attempts) throw error;
        await new Promise((resolve) => setTimeout(resolve, 800 * attempt));
      }
    }
  };

  const money = (cents) => {
    const digits = cents % 100 === 0 ? 0 : 2;
    return `${(cents / 100).toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: 2 })} Kč`;
  };

  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[char]));

  const fold = (value) => String(value).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

  const localPath = (src) => src ? src.replace(/^(https?:)?\/\/[^/]+/, '') : '';

  const listsLabel = (count) => {
    if(count === 1) return '1 grafický list';
    return count <= 4 ? `${count} grafické listy` : `${count} grafických listů`;
  };

  const worksLabel = (count) => {
    if(count === 1) return '1 dostupné dílo';
    return count >= 2 && count <= 4 ? `${count} dostupná díla` : `${count} dostupných děl`;
  };

  /* The feed comes best-selling first, but unsold lists tie and stay in author blocks: keep the order, space out the authors. */
  const spreadAuthors = (items) => {
    const pending = [...items];
    const spread = [];
    while(pending.length){
      const recent = new Set(spread.slice(-AUTHOR_GAP).map(({ author }) => author));
      const next = pending.findIndex(({ author }) => !recent.has(author));
      spread.push(...pending.splice(Math.max(next, 0), 1));
    }
    return spread;
  };

  /* Print size in cm from the product text, e.g. "Autorizovaná umělecká reprodukce, 50 x 70 cm." */
  const parseSize = (text) => {
    const match = String(text).match(/(\d{2,3}(?:[.,]\d)?)\s*[x×X]\s*(\d{2,3}(?:[.,]\d)?)/);
    if(!match) return null;
    const sides = [match[1], match[2]].map((side) => parseFloat(side.replace(',', '.')));
    const short = Math.min(...sides);
    let long = Math.max(...sides);
    if(long > 200) long /= 10;
    return { short, long };
  };

  const frameGroup = (size) => {
    if(!size) return null;
    if(size.short <= 40 && size.long <= 57) return 'S';
    if(size.short <= 50 && size.long <= 70) return 'M';
    if(size.short <= 56 && size.long <= 76) return 'L';
    return null;
  };

  /* The second product image shows the whole sheet on a flat backdrop: find the sheet in it. */
  const sheetRects = new Map();
  const detectSheet = (src) => {
    if(sheetRects.has(src)) return sheetRects.get(src);
    const result = new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onerror = () => resolve(null);
      image.onload = () => {
        const width = 200;
        const height = Math.round(width * image.naturalHeight / image.naturalWidth);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(image, 0, 0, width, height);
        const data = context.getImageData(0, 0, width, height).data;
        const pixel = (x, y) => data.subarray((y * width + x) * 4, (y * width + x) * 4 + 3);
        const corners = [pixel(2, 2), pixel(width - 3, 2), pixel(2, height - 3), pixel(width - 3, height - 3)];
        const channels = [0, 1, 2].map((channel) => corners.map((corner) => corner[channel]).sort((a, b) => a - b));
        if(channels.some((channel) => channel[3] - channel[0] > 24)) return resolve(null);
        const backdrop = channels.map((channel) => channel[1]);
        const rows = new Array(height).fill(0);
        const columns = new Array(width).fill(0);
        for(let y = 0; y < height; y++){
          for(let x = 0; x < width; x++){
            const color = pixel(x, y);
            if(Math.abs(color[0] - backdrop[0]) + Math.abs(color[1] - backdrop[1]) + Math.abs(color[2] - backdrop[2]) > 30){
              rows[y]++;
              columns[x]++;
            }
          }
        }
        const left = columns.findIndex((count) => count > height * 0.25);
        const top = rows.findIndex((count) => count > width * 0.25);
        const right = width - 1 - [...columns].reverse().findIndex((count) => count > height * 0.25);
        const bottom = height - 1 - [...rows].reverse().findIndex((count) => count > width * 0.25);
        if(left < 4 || top < 4 || right > width - 5 || bottom > height - 5) return resolve(null);
        resolve({
          x: left / width,
          y: top / height,
          width: (right - left + 1) / width,
          height: (bottom - top + 1) / height,
          ratio: (right - left + 1) / (bottom - top + 1)
        });
      };
      image.src = localPath(src);
    });
    sheetRects.set(src, result);
    return result;
  };

  class GomaPack extends HTMLElement {
    connectedCallback(){
      this.config = JSON.parse(this.querySelector('[data-pack-config]').textContent);
      this.root = this.querySelector('[data-pack-root]');
      this.refs = {};
      this.root.querySelectorAll('[data-ref]').forEach((element) => {
        this.refs[element.dataset.ref] = element;
      });
      this.feedItems = [];
      this.items = [];
      this.inCart = new Set();
      this.feedLoading = true;
      this.state = { count: this.config.defaultTier, picks: [], query: '', author: '', limit: PAGE_SIZE, view: 'pick' };
      this.frameChoices = this.buildFrameChoices();

      this.addEventListener('click', this.onClick.bind(this));
      this.root.addEventListener('input', this.onFilter.bind(this));
      this.root.addEventListener('close', () => document.documentElement.classList.remove('gp-open'));
      if(this.dataset.mode === 'page') this.start();
    }

    /* Colour options and the price per frame size, from the frame product (or demo values in a preview). */
    buildFrameChoices(){
      if(!this.config.framesEnabled) return [];
      if(this.config.demoFrames){
        return FRAME_LOOKS.map(({ look, label }) => ({
          look, label, demo: true,
          variants: Object.entries(DEMO_FRAME_PRICES).map(([size, price]) => ({ id: null, size, price, available: true }))
        }));
      }
      const choices = new Map();
      this.config.frames.forEach((variant) => {
        const known = FRAME_LOOKS.find(({ test }) => test.test(fold(variant.color)));
        if(!choices.has(variant.color)){
          choices.set(variant.color, { look: known ? known.look : 'grey', label: known ? known.label : `Rám ${variant.color}`, variants: [] });
        }
        choices.get(variant.color).variants.push(variant);
      });
      return [...choices.values()];
    }

    start(){
      this.loadCart();
      if(this.started) return this.renderAll();
      this.started = true;
      this.renderAll();
      this.loadFeed(this.config.feed, (items) => {
        this.feedItems.push(...items);
        this.items = spreadAuthors(this.feedItems);
        this.fillAuthors();
        this.renderGrid();
        this.renderSummary();
      });
      this.observer = new IntersectionObserver((entries) => {
        if(entries.some((entry) => entry.isIntersecting) && this.state.limit < this.filtered().length){
          this.state.limit += PAGE_SIZE;
          this.renderGrid();
        }
      }, { root: this.dataset.mode === 'dialog' ? this.root.querySelector('.gp_main') : null, rootMargin: '400px' });
      this.observer.observe(this.refs.sentinel);
    }

    async loadFeed(url, onPage){
      try {
        let page = 1;
        let pages = 1;
        while(page <= pages){
          const data = await fetchJson(`${url}&page=${page}`);
          pages = data.pages || 1;
          onPage(data.items);
          page++;
        }
      } catch(error) {
        console.error(error);
        this.feedFailed = true;
        this.refs.count.textContent = this.countLabel(0);
      }
      this.feedLoading = false;
      this.renderGrid();
    }

    /* A list already in the cart stays out of the pack: as the last piece it would fail the whole add. */
    async loadCart(){
      try {
        const cart = await fetchJson(`${window.routes.cart_url}.js`, 1);
        this.inCart = new Set(cart.items.map(({ variant_id }) => variant_id));
      } catch(error) {
        console.error(error);
      }
      this.state.picks = this.state.picks.filter(({ item }) => !this.inCart.has(item.id));
      this.preselectCurrent();
      this.renderAll();
    }

    preselectCurrent(){
      const { current } = this.config;
      if(!current || this.preselected || this.inCart.has(current.id)) return;
      this.preselected = true;
      this.addPick(current);
    }

    countLabel(count){
      if(this.feedFailed) return 'Některá díla se nenačetla. Zkuste obnovit stránku.';
      return this.feedLoading ? `${worksLabel(count)}, načítám další…` : worksLabel(count);
    }

    fillAuthors(){
      const counts = new Map();
      this.items.forEach((item) => counts.set(item.author, (counts.get(item.author) || 0) + 1));
      const select = this.root.querySelector('[data-filter="author"]');
      const selected = select.value;
      const options = [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0], 'cs'))
        .map(([author, count]) => `<option value="${escapeHtml(author)}">${escapeHtml(author)} (${count})</option>`);
      select.innerHTML = `<option value="">Všichni autoři</option>${options.join('')}`;
      select.value = selected;
    }

    filtered(){
      const query = fold(this.state.query.trim());
      return this.items.filter((item) => {
        if(this.state.author && item.author !== this.state.author) return false;
        return !query || fold(`${item.title} ${item.author}`).includes(query);
      });
    }

    tier(){
      return this.config.tiers.find((tier) => tier.count === this.state.count);
    }

    slotCount(tier = this.tier()){
      return tier.count + tier.free;
    }

    /* The pack price: paid lists minus the tier discount, the free lists cost nothing. */
    packPrices(){
      const { picks } = this.state;
      const tier = this.tier();
      const fallback = (picks[0] ? picks[0].item : this.items[0] || { price: 0 }).price;
      const paid = Array.from({ length: tier.count }, (_, index) => (picks[index] ? picks[index].item.price : fallback));
      const listsTotal = paid.reduce((sum, price) => sum + price, 0);
      const saved = paid.reduce((sum, price) => sum + Math.round(price * tier.percent / 100), 0);
      /* Past the largest pack every further list keeps that pack's price per list. */
      const extra = Math.max(picks.length - this.slotCount(tier), 0);
      return { tier, listsTotal, saved, extra, extraTotal: Math.round(extra * (listsTotal - saved) / this.slotCount(tier)) };
    }

    trimPicks(){
      const { tiers } = this.config;
      if(this.state.count !== tiers[tiers.length - 1].count) this.state.picks.length = Math.min(this.state.picks.length, this.slotCount());
    }

    framePrice(variant){
      return Math.round(variant.price * (100 - this.config.frameOff) / 100);
    }

    frameVariant(pick){
      const choice = this.frameChoices.find(({ look }) => look === pick.frame);
      const group = frameGroup(pick.size);
      if(!choice || !group) return null;
      return choice.variants.find((variant) => variant.size === group || fold(variant.size).startsWith(fold(group))) || null;
    }

    addPick(item){
      const { picks } = this.state;
      const { tiers } = this.config;
      const pick = { item, frame: null, size: parseSize(item.text), rect: null };
      picks.push(pick);
      if(picks.length > this.slotCount()) this.state.count = (tiers.find((tier) => this.slotCount(tier) >= picks.length) || tiers[tiers.length - 1]).count;
      this.showError('');
      if(item.sheet){
        detectSheet(item.sheet).then((rect) => {
          pick.rect = rect;
          this.renderSide();
        });
      }
      this.syncPicked();
      this.renderSide();
    }

    removePick(index){
      this.state.picks.splice(index, 1);
      this.syncPicked();
      this.renderSide();
    }

    setTier(count){
      this.state.count = count;
      this.trimPicks();
      this.syncPicked();
      this.renderSide();
    }

    onFilter(event){
      const key = event.target.dataset.filter;
      if(!key) return;
      this.state[key] = event.target.value;
      this.state.limit = PAGE_SIZE;
      this.renderGrid();
    }

    onClick(event){
      const button = event.target.closest('[data-action]');
      if(!button) return;
      const { action, count, id, index, look } = button.dataset;
      if(action === 'open') return this.open(Number(count));
      if(action === 'close') return this.root.close();
      if(action === 'tier') return this.setTier(Number(count));
      if(action === 'remove') return this.removePick(Number(index));
      if(action === 'add') return this.addToCart();
      if(action === 'view'){
        this.state.view = button.dataset.view;
        return this.renderSide();
      }
      if(action === 'frame'){
        this.state.picks[Number(index)].frame = look || null;
        return this.renderSide();
      }
      if(action === 'pick'){
        const picked = this.state.picks.findIndex(({ item }) => item.id === Number(id));
        if(picked > -1) return this.removePick(picked);
        this.addPick(this.items.find((item) => item.id === Number(id)));
      }
    }

    open(count){
      this.state.count = count;
      this.trimPicks();
      this.start();
      this.root.showModal();
      document.documentElement.classList.add('gp-open');
    }

    renderAll(){
      this.renderGrid();
      this.renderSide();
    }

    renderGrid(){
      const items = this.filtered();
      const visible = items.slice(0, this.state.limit);
      const key = visible.map((item) => item.id).join();
      if(this.items.length) this.refs.count.textContent = this.countLabel(items.length);
      if(key !== this.gridKey){
        this.gridKey = key;
        this.refs.grid.innerHTML = visible.map((item) => `
          <div class="gp-card" data-card="${item.id}">
            <button type="button" class="gp-card_media" data-action="pick" data-id="${item.id}" aria-label="${escapeHtml(item.title)}">
              ${item.image ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy" width="360" height="360">` : ''}
              <span class="gp-card_badge"></span>
            </button>
            <span class="gp-card_author">${escapeHtml(item.author)}</span>
            <a class="gp-card_title" href="/products/${escapeHtml(item.handle)}" target="_blank" rel="noopener">${escapeHtml(item.title)}</a>
            <span class="gp-card_price">${money(item.price)}</span>
          </div>`).join('');
        /* A tall screen can keep the sentinel in reach after a render, and the observer reports only changes. */
        if(this.observer){
          this.observer.unobserve(this.refs.sentinel);
          this.observer.observe(this.refs.sentinel);
        }
      }
      this.syncPicked();
    }

    syncPicked(){
      const picked = new Set(this.state.picks.map(({ item }) => item.id));
      this.refs.grid.querySelectorAll('[data-card]').forEach((card) => {
        const id = Number(card.dataset.card);
        const isPicked = picked.has(id);
        const isInCart = this.inCart.has(id);
        const media = card.querySelector('.gp-card_media');
        card.classList.toggle('gp-card--picked', isPicked);
        card.classList.toggle('gp-card--in-cart', isInCart);
        media.setAttribute('aria-pressed', isPicked);
        media.disabled = isInCart;
        card.querySelector('.gp-card_badge').textContent = isInCart ? 'V košíku' : isPicked ? 'Vybráno' : '+ Přidat';
      });
    }

    renderSide(){
      const { picks, count, view } = this.state;
      const slots = Math.max(this.slotCount(), picks.length);
      const isFree = (index) => index >= count && index < this.slotCount();
      this.root.dataset.view = view;
      this.refs.tiers.innerHTML = this.config.tiers.map((tier) => `
        <button type="button" class="gp-tier" data-action="tier" data-count="${tier.count}" aria-pressed="${tier.count === count}">
          <span class="gp-tier_count">${listsLabel(tier.count)}</span>
          <span class="gp-tier_save">−${tier.percent} %</span>
          ${tier.free > 0 ? `<span class="gp-tier_free">+ ${tier.free} zdarma</span>` : ''}
        </button>`).join('');

      this.refs.wall.classList.toggle('gp_wall--large', slots > 5);
      this.refs.wall.innerHTML = Array.from({ length: slots }, (_, index) => {
        if(picks[index]) return this.framedHtml(picks[index]);
        return `<span class="gp-framed gp-framed--empty${isFree(index) ? ' gp-framed--free' : ''}" aria-hidden="true"></span>`;
      }).join('');

      this.refs.slots.innerHTML = Array.from({ length: slots }, (_, index) => {
        if(picks[index]) return this.slotHtml(picks[index], index, isFree(index));
        return `<p class="gp-slot gp-slot--empty${isFree(index) ? ' gp-slot--free' : ''}">Vyberte ${index + 1}. grafický list${isFree(index) ? ' zdarma' : ''}</p>`;
      }).join('');

      this.renderSummary();
    }

    framedHtml(pick){
      const { item, rect, frame, size } = pick;
      const source = rect ? item.sheet : item.image;
      const ratio = rect ? rect.ratio : 1;
      const { short, long } = size || { short: 50, long: 70 };
      const crop = rect
        ? `width:${100 / rect.width}%;height:${100 / rect.height}%;left:${-100 * rect.x / rect.width}%;top:${-100 * rect.y / rect.height}%`
        : 'width:100%;height:100%;left:0;top:0';
      return `
        <span class="gp-framed gp-framed--${frame || 'none'}" style="--gp-ratio:${ratio.toFixed(3)};--gp-width:${ratio > 1 ? long : short}">
          <span class="gp-framed_sheet"><img src="${escapeHtml(source)}" alt="${escapeHtml(item.title)}" style="${crop}"></span>
        </span>`;
    }

    slotHtml(pick, index, isFree){
      const { item, frame } = pick;
      const variant = this.frameVariant(pick);
      const framable = this.frameChoices.length > 0 && frameGroup(pick.size);
      const swatches = framable ? `
        <div class="gp-slot_frames" role="group" aria-label="Rám">
          <button type="button" class="gp-swatch gp-swatch--none" data-action="frame" data-index="${index}" data-look="" aria-pressed="${!frame}" title="Bez rámu"></button>
          ${this.frameChoices.map(({ look, label }) => `
            <button type="button" class="gp-swatch gp-swatch--${look}" data-action="frame" data-index="${index}" data-look="${look}" aria-pressed="${frame === look}" title="${escapeHtml(label)}"></button>`).join('')}
          <span class="gp-slot_frame-label">${variant ? `${this.frameChoices.find(({ look }) => look === frame).label} +${money(this.framePrice(variant))}` : 'Bez rámu'}</span>
        </div>` : '';
      return `
        <div class="gp-slot">
          <div class="gp-slot_info">
            <span class="gp-slot_title">${escapeHtml(item.title)}</span>
            <span class="gp-slot_author">${escapeHtml(item.author)}${isFree ? ' · <strong class="gp-slot_free">zdarma</strong>' : ''}</span>
          </div>
          <button type="button" class="gp-slot_remove" data-action="remove" data-index="${index}" aria-label="Odebrat ${escapeHtml(item.title)}">×</button>
          ${swatches}
        </div>`;
    }

    renderSummary(){
      const { picks, view } = this.state;
      const { tier, listsTotal, saved, extra, extraTotal } = this.packPrices();
      const frames = picks.map((pick) => this.frameVariant(pick)).filter(Boolean);
      const framesTotal = frames.reduce((sum, variant) => sum + this.framePrice(variant), 0);
      const total = listsTotal - saved + extraTotal + framesTotal;
      const slots = Math.max(this.slotCount(), picks.length);
      const missing = slots - picks.length;
      const rows = [
        `<div class="gp-row"><span>Grafické listy (${tier.count}×)</span><span>${money(listsTotal)}</span></div>`,
        `<div class="gp-row gp-row--save"><span>Sleva ${tier.percent} %</span><span>−${money(saved)}</span></div>`
      ];
      if(tier.free) rows.push(`<div class="gp-row gp-row--save"><span>Grafické listy zdarma (${tier.free}×)</span><span>0 Kč</span></div>`);
      if(extra) rows.push(`<div class="gp-row"><span>Další grafické listy (${extra}×)</span><span>${money(extraTotal)}</span></div>`);
      if(frames.length) rows.push(`<div class="gp-row"><span>Rámy (${frames.length}×)</span><span>${money(framesTotal)}</span></div>`);
      rows.push(`<div class="gp-row gp-row--total"><span>Celkem</span><strong>${money(total)}</strong></div>`);
      this.refs.summary.innerHTML = rows.join('');

      this.refs.cta.disabled = missing > 0 || this.busy;
      this.refs.cta.textContent = missing > 0 ? `Vyberte ještě ${listsLabel(missing)}` : 'Přidat sadu do košíku';
      this.refs.bar.innerHTML = view === 'pick' ? `
        <button type="button" class="gp-bar_toggle" data-action="view" data-view="pack">
          <span>Moje sada ${picks.length}/${slots}</span>
          <strong>${money(total)}</strong>
        </button>` : `
        <button type="button" class="gp-bar_toggle" data-action="view" data-view="pick">
          <span>Zpět na výběr děl</span>
        </button>`;
    }

    showError(message){
      this.refs.error.textContent = message;
      this.refs.error.hidden = !message;
    }

    buildCartItems(){
      const packId = `${Date.now().toString(36)}-${this.state.count}`;
      const items = [];
      this.state.picks.forEach((pick) => {
        const props = { _pack: packId };
        const variant = this.frameVariant(pick);
        const choice = this.frameChoices.find(({ look }) => look === pick.frame);
        if(variant && variant.id){
          items.push({ id: variant.id, quantity: 1, properties: { 'K dílu': `${pick.item.title} (${pick.item.author})`, _pack: packId } });
        } else if(variant) {
          props['Rám (ukázka)'] = `${choice.label}, velikost ${variant.size}`;
        }
        items.push({ id: pick.item.id, quantity: 1, properties: props });
      });
      return items;
    }

    /* A list can sell out while the builder is open: take it out of the pack and of the offer. */
    async dropSoldOut(){
      const picks = [...this.state.picks];
      const checks = await Promise.all(picks.map(({ item }) => fetch(`/products/${item.handle}.js`)
        .then((response) => (response.ok ? response.json() : { available: response.status !== 404 }))
        .catch(() => ({ available: true }))));
      const soldOut = new Set(picks.filter((pick, index) => !checks[index].available).map(({ item }) => item.id));
      if(!soldOut.size) return;
      this.state.picks = this.state.picks.filter(({ item }) => !soldOut.has(item.id));
      this.feedItems = this.feedItems.filter(({ id }) => !soldOut.has(id));
      this.items = spreadAuthors(this.feedItems);
      this.fillAuthors();
      this.renderGrid();
      this.renderSide();
    }

    async addToCart(){
      this.busy = true;
      this.showError('');
      this.refs.cta.disabled = true;
      this.refs.cta.textContent = 'Přidávám…';
      try {
        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: this.buildCartItems(),
            sections: CART_SECTIONS.map(({ id }) => id),
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();
        if(response.status === 422){
          await this.dropSoldOut();
          await this.loadCart();
          return this.showError(data.description || data.message || ADD_FAILED);
        }
        if(!response.ok) throw new Error(`Cart add: ${response.status}`);
        this.afterAdd(data);
      } catch(error) {
        console.error(error);
        this.showError(ADD_FAILED);
      } finally {
        this.busy = false;
        this.renderSummary();
      }
    }

    afterAdd(data){
      if(this.root.open) this.root.close();
      this.state.picks.forEach(({ item }) => this.inCart.add(item.id));
      this.state.picks = [];
      this.state.view = 'pick';
      this.preselected = true;
      this.renderAll();
      const drawer = document.querySelector('[data-modal-ref="modal-cart-drawer"][data-expanded="false"]');
      if(typeof sectionArrRender !== 'function' || !drawer) return window.location.assign(window.routes.cart_url);
      try {
        sectionArrRender(CART_SECTIONS, data);
        drawer.dispatchEvent(new Event('click'));
      } catch(error) {
        console.error(error);
        window.location.assign(window.routes.cart_url);
      }
    }
  }

  if(!customElements.get('goma-pack')) customElements.define('goma-pack', GomaPack);
})();
